import { layout } from "@app/domain";
import type { WireCard } from "@app/protocol";
import { useEffect, useRef } from "react";
import { Card, CardBack } from "./Card";
import { playDeal } from "./dealMotion";
import type { DealRun } from "./useDeal";

/** The three cards of the deck, a little apart (design, in 72 px card units). */
const DECK: readonly (readonly [number, number])[] = [
  [0, 0],
  [2, 2.5],
  [4, 5],
];

/** A circle centred on a point, for the flash and the rings. */
function circle(at: layout.Point, size: number) {
  return { left: at.x - size / 2, top: at.y - size / 2, width: size, height: size };
}

/**
 * The deal (handoff "Patlama"): the deck charges and bursts, the cards fly to the
 * seats a round at a time, yours turn face up and land in your hand, the others
 * vanish into the pods. Drawn on the canvas; the motion lives in `dealMotion`.
 */
export function Deal({
  run,
  canvas,
  hand,
  onDone,
}: {
  run: DealRun;
  canvas: layout.Canvas;
  hand: readonly WireCard[];
  onDone: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => playDeal(root.current, run, onDone), [run, onDone]);
  const centre = layout.deckCentre(canvas);
  const card = canvas.trickCard;
  const k = card.w / 72;
  const burst = run.timing.charge > 0;
  return (
    <div
      ref={root}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-30 [perspective:900px]"
    >
      {burst
        ? DECK.map(([dx, dy]) => (
            <div
              key={`${dx}-${dy}`}
              data-deal="deck"
              className="absolute"
              style={{ left: centre.x - card.w / 2 + dx * k, top: centre.y - card.h / 2 + dy * k }}
            >
              <CardBack width={card.w} height={card.h} />
            </div>
          ))
        : null}
      {burst ? (
        <>
          <span
            data-deal="flash"
            className="deal-flash absolute rounded-full opacity-0"
            style={circle(centre, 220 * k)}
          />
          <span
            data-deal="ring"
            className="deal-ring absolute rounded-full opacity-0"
            style={circle(centre, 200 * k)}
          />
          {layout.DEAL_ORDER.map((slot) => (
            <span
              key={slot}
              data-deal="impact"
              data-slot={slot}
              className="deal-impact absolute rounded-full opacity-0"
              style={circle(layout.podCentre(canvas, slot), canvas.pod.size + 12)}
            />
          ))}
        </>
      ) : null}
      {run.flights.map((f) => {
        const face = f.handIndex === null ? undefined : hand[f.handIndex];
        return (
          <div
            key={f.id}
            data-id={f.id}
            className="absolute opacity-0 [transform-style:preserve-3d] [will-change:transform,opacity]"
            style={{
              left: centre.x - card.w / 2,
              top: centre.y - card.h / 2,
              width: card.w,
              height: card.h,
              zIndex: 10 + f.round,
            }}
          >
            <div className="relative h-full w-full [transform-style:preserve-3d]">
              <div className="absolute inset-0 [backface-visibility:hidden]">
                <CardBack width={card.w} height={card.h} />
              </div>
              {face ? (
                <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                  <Card card={face} width={card.w} height={card.h} state="plain" />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
